/**
 * AppInput — the app's standard text input, built on Fluent UI v9 Input + Field.
 *
 * Wraps Fluent's `Field` + `Input` pair so the label is semantically associated
 * with the control. Styled via Griffel makeStyles + tokens to match the Utah
 * Health brand (consistent with AppButton, DiscoveryEvalForm field style).
 *
 * No SCSS. No raw HTML inputs.
 */

import {
  Field,
  Input,
  makeStyles,
  mergeClasses,
  tokens,
  type InputProps,
} from '@fluentui/react-components';

export interface AppInputProps extends Omit<InputProps, 'onChange'> {
  /** Semantic label rendered by Fluent Field (required for a11y). */
  label: string;
  value?: string;
  onChange?: (val: string) => void;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  className?: string;
}

const useStyles = makeStyles({
  input: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
    ':focus-within': {
      outlineColor: tokens.colorBrandBackground,
    },
  },
});

export function AppInput({
  label,
  value,
  onChange,
  placeholder,
  maxLength,
  disabled,
  className,
  ...rest
}: AppInputProps) {
  const styles = useStyles();

  return (
    <Field label={label}>
      <Input
        value={value}
        onChange={(_, d) => onChange?.(d.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        className={mergeClasses(styles.input, className)}
        {...rest}
      />
    </Field>
  );
}

export default AppInput;
